import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageSquare } from 'lucide-react';
import { getMyOrders } from '../../services/p2pService';
import { useAuth } from '../../context/AuthContext';
import { useP2P } from '../../context/P2PContext';

export default function ChatList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { unreadCounts } = useP2P();
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMyOrders().then(res => setOrders(res.orders ?? [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const ordersWithMsgs = orders
    .filter(o => o.messages?.length > 0)
    .sort((a, b) => {
      const aLast = a.messages?.[a.messages.length - 1]?.createdAt ?? a.createdAt;
      const bLast = b.messages?.[b.messages.length - 1]?.createdAt ?? b.createdAt;
      return new Date(bLast).getTime() - new Date(aLast).getTime();
    });

  return (
    <div className="p2p-page">
      {/* Sticky header */}
      <div className="p2p-header">
        <button onClick={() => navigate('/home')} style={{ background: 'none', border: 'none', color: 'var(--p2p-text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px 8px 4px 0' }}>
          <ArrowLeft size={20} />
        </button>
        <span className="p2p-header-title">Messages</span>
        <div />
      </div>

      {/* Scrollable content */}
      <div className="p2p-page-content">
        {loading ? (
          <>
            {[...Array(4)].map((_, i) => (
              <div key={i} style={{ padding: '12px 16px', borderBottom: '1px solid var(--p2p-border-default)', display: 'flex', alignItems: 'center', gap: 12 }}>
                <span className="p2p-skel" style={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0 }} />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <span className="p2p-skel" style={{ width: '50%', height: 13 }} />
                  <span className="p2p-skel" style={{ width: '75%', height: 11 }} />
                </div>
                <span className="p2p-skel" style={{ width: 35, height: 10, flexShrink: 0 }} />
              </div>
            ))}
          </>
        ) : ordersWithMsgs.length === 0 ? (
          <div className="p2p-empty">
            <MessageSquare size={40} className="p2p-empty-icon" />
            <p className="p2p-empty-text">No conversations yet</p>
          </div>
        ) : (
          <>
            {ordersWithMsgs.map(order => {
              const isBuyer = order.buyer?._id === user?._id;
              const counterparty = isBuyer ? order.seller : order.buyer;
              const lastMsg = order.messages?.[order.messages.length - 1];
              const unread = unreadCounts[order._id] ?? 0;
              return (
                <div
                  key={order._id}
                  style={{ display: 'flex', gap: 12, padding: '14px 16px', borderBottom: '1px solid var(--p2p-border-default)', cursor: 'pointer', alignItems: 'center' }}
                  onClick={() => navigate(`/p2p/order/${order._id}`)}
                >
                  <div className="p2p-avatar" style={{ overflow: 'hidden', padding: 0 }}>
                    {counterparty?.avatar
                      ? <img src={counterparty.avatar} alt={counterparty?.nickname ?? counterparty?.name ?? '?'} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                      : (counterparty?.nickname ?? counterparty?.name ?? '?').slice(0, 1).toUpperCase()
                    }
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--p2p-text-primary)' }}>{counterparty?.nickname ?? counterparty?.name ?? 'Unknown'}</span>
                      <span style={{ fontSize: 11, color: 'var(--p2p-text-muted)' }}>
                        {lastMsg?.createdAt ? new Date(lastMsg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2 }}>
                      <span style={{ fontSize: 13, color: 'var(--p2p-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
                        {lastMsg?.type === 'image' ? '📎 Photo' : lastMsg?.type === 'system' ? `🔔 ${lastMsg.text}` : (lastMsg?.text ?? '')}
                      </span>
                      {unread > 0 && (
                        <span style={{ background: 'var(--p2p-sell)', color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 9999, padding: '1px 6px', minWidth: 18, textAlign: 'center', flexShrink: 0 }}>
                          {unread}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
